import React, { Fragment, useState, ReactElement, useMemo } from "react";
import { cloneDeep } from "lodash";
import { Formik, Form, FieldArray } from "formik";
import { Button } from "../../../../../components/src/Button";
import { Alert, AlertVariant } from "../../../../../components/src/Alert";

import { recursivelyRemoveKey } from "../../../../../common/src";

import { useAlertTimeOut } from "../../../../../components/src/common/Utils";
import { SubmitButton, TextField, DateField, SelectField, CheckboxItem } from "../../../../../components/src/form";
import { useMutation } from "urql";
import { UPDATE_PARTY_ISSUER } from "../../../../../graphql/src/party/queries";
import { PartyPageData } from "../PartyPage";
import {
    EngagementMethod,
    IorpEnum,
    ProductInvolvementIndicator,
    RangeEnum,
    StandardScreenNorm,
    StandardScreenPrinciple,
    StandardScreenStatus,
    ScienceBasedTargetInitiativeEnum
} from "../../../types.generated";
import { formikUrqlErrorFormater } from "../../../../../components/src/common/formik-urql-error-helper";
import { Grid, Column } from "../../../../../components/src";
import { Link, useParams } from "wouter";
import { MarkDownField } from "../../../components/form/MarkDownField";
import { REACT_APP_API_URI } from "../../../env";

export const PartyIssuerTab = ({ data, refetch }: { data: PartyPageData; refetch: () => void }): ReactElement => {
    const { id: partyId } = useParams<{ id: string; tabId: string }>();

    const [_, updatePartyIssuer] = useMutation(UPDATE_PARTY_ISSUER);
    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    useAlertTimeOut(alert, setAlert, 5);

    const currentParty = useMemo(() => {
        if (data) {
            const p = cloneDeep(data.party);
            if (p.issuer) {
                p.issuer.transitionCompany = p.issuer.transitionCompany || false;
                p.issuer.keySector = p.issuer.keySector || false;
                p.issuer.captorDnshCompliant = p.issuer.captorDnshCompliant || false;
                p.issuer.ghgEmissions = p.issuer.ghgEmissions || false;
                p.issuer.carbonFootprint = p.issuer.carbonFootprint || false;
                p.issuer.ghgIntensityOfInvesteeCompanies = p.issuer.ghgIntensityOfInvesteeCompanies || false;
                p.issuer.fossilFuelCompaniesExposure = p.issuer.fossilFuelCompaniesExposure || false;
                p.issuer.nonRenewableEnergyShare = p.issuer.nonRenewableEnergyShare || false;
                p.issuer.energyIntensityHighImpact = p.issuer.energyIntensityHighImpact || false;
                p.issuer.activitiesAffectingBiodiversityAreas = p.issuer.activitiesAffectingBiodiversityAreas || false;
                p.issuer.pollutionOfWater = p.issuer.pollutionOfWater || false;
                p.issuer.hazardousWasteRatio = p.issuer.hazardousWasteRatio || false;
                p.issuer.deforestation = p.issuer.deforestation || false;
                p.issuer.recordableWorkAccidents = p.issuer.recordableWorkAccidents || false;
                p.issuer.lackOfProcessesAndCompliance = p.issuer.lackOfProcessesAndCompliance || false;
                p.issuer.unadjustedGenderPayGap = p.issuer.unadjustedGenderPayGap || false;
                p.issuer.managementSupervisoryGenderDiversity = p.issuer.managementSupervisoryGenderDiversity || false;
                p.issuer.scienceBasedTargetInitiative = p.issuer.scienceBasedTargetInitiative || ScienceBasedTargetInitiativeEnum.NoTarget;
                p.issuer.nearTermTargetClassification = p.issuer.nearTermTargetClassification || "";
                p.issuer.netZeroCommitted = p.issuer.netZeroCommitted || false;
            }
            return p;
        }
        return null;
    }, [data]);

    if (!currentParty) return <div></div>;

    const defaultGlobalStandardScreensDefinition = {
        status: StandardScreenStatus.Compliant,
        norm: StandardScreenNorm.None,
        date: "",
        principle: StandardScreenPrinciple.None,
        comment: ""
    };
    const defaultShareholderEngagementsDefinition = {
        method: EngagementMethod.Voting,
        norm: StandardScreenNorm.None,
        date: "",
        agm: "",
        principle: StandardScreenPrinciple.None,
        comment: ""
    };

    const defaultCommentsDefinition = {
        date: "",
        comment: ""
    };

    const defaultProductInvolvementDefinition = {
        involvement: ProductInvolvementIndicator.AdultEntertainment,
        range: RangeEnum.R0To5
    };

    return (
        <div id="partyissuerform" className="partyissuerform form mx-3">
            <Formik
                enableReinitialize={true}
                initialValues={
                    currentParty.issuer
                        ? currentParty.issuer
                        : {
                              iorp: IorpEnum.None,
                              keySector: false,
                              transitionCompany: false,
                              captorDnshCompliant: false,
                              ghgEmissions: false,
                              carbonFootprint: false,
                              ghgIntensityOfInvesteeCompanies: false,
                              fossilFuelCompaniesExposure: false,
                              nonRenewableEnergyShare: false,
                              energyIntensityHighImpact: false,
                              activitiesAffectingBiodiversityAreas: false,
                              pollutionOfWater: false,
                              hazardousWasteRatio: false,
                              deforestation: false,
                              recordableWorkAccidents: false,
                              lackOfProcessesAndCompliance: false,
                              unadjustedGenderPayGap: false,
                              managementSupervisoryGenderDiversity: false,
                              scienceBasedTargetInitiative: ScienceBasedTargetInitiativeEnum.NoTarget,
                              netZeroCommitted: false,
                              nearTermTargetClassification: "",
                              esg: {
                                  globalStandardScreen: [],
                                  shareholderEngagement: [],
                                  comments: [],
                                  productInvolvement: []
                              }
                          }
                }
                onSubmit={async (submitValues, { setSubmitting, setErrors }) => {
                    const input: any = recursivelyRemoveKey(submitValues, "__typename");

                    await updatePartyIssuer({ partyId: partyId, input })
                        .then((result) => {
                            if ("error" in result && result.error) {
                                const message = formikUrqlErrorFormater(result.error, setErrors);
                                setAlert({ color: "danger", visible: true, message });
                            } else {
                                setAlert({ color: "success", visible: true, message: `The party has been updated successfully!` });
                                refetch();
                            }
                        })
                        .catch((error) => {
                            setAlert({ color: "danger", visible: true, message: error.toString() });
                        })
                        .finally(() => {
                            setSubmitting(false);
                        });
                }}
            >
                {({ isSubmitting, values }) => {
                    return (
                        <Fragment>
                            <div className="flex flex-wrap -mx-2">
                                <div className="w-full md:w-6/12 px-2 mb-4">
                                    <Form autoComplete="off">
                                        <div className="flex flex-wrap -mx-2">
                                            <SelectField
                                                name="iorp"
                                                label="Iorp"
                                                options={Object.keys(IorpEnum).sort()}
                                                className=""
                                                disabled={isSubmitting}
                                                size={12}
                                            />
                                        </div>
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 8 }}>
                                            <CheckboxItem name="keySector" label="Key sector" />
                                        </div>
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 8 }}>
                                            <CheckboxItem name="transitionCompany" label="Transition company" />
                                        </div>
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 8 }}>
                                            <CheckboxItem
                                                name="captorDnshCompliant"
                                                label="Compliant with Captor DNSH requirement for a transition company"
                                            />
                                        </div>
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 8 }}>
                                            <label className="font-bold">PAI considered</label>
                                        </div>
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 12 }}>
                                            <p>
                                                <u>Climate and other environmental related indicators</u>
                                            </p>
                                        </div>

                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 4 }}>
                                            <CheckboxItem name="ghgEmissions" label="1. GHG emissions" />
                                        </div>
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 8 }}>
                                            <CheckboxItem name="carbonFootprint" label="2. Carbon footprint" />
                                        </div>
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 8 }}>
                                            <CheckboxItem
                                                name="ghgIntensityOfInvesteeCompanies"
                                                label="3. GHG intensity of investee companies"
                                            />
                                        </div>
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 8 }}>
                                            <CheckboxItem
                                                name="fossilFuelCompaniesExposure"
                                                label="5. Exposure to companies in the fossil fuel sector"
                                            />
                                        </div>
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 8 }}>
                                            <CheckboxItem
                                                name="nonRenewableEnergyShare"
                                                label="6. Share of non-renewable energy consumption and production"
                                            />
                                        </div>
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 8 }}>
                                            <CheckboxItem
                                                name="energyIntensityHighImpact"
                                                label="7. Energy consumption intensity per high impact climate sector"
                                            />
                                        </div>
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 8 }}>
                                            <CheckboxItem
                                                name="activitiesAffectingBiodiversityAreas"
                                                label="8. Activities negatively affecting biodiversity sensitive areas"
                                            />
                                        </div>
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 8 }}>
                                            <CheckboxItem name="pollutionOfWater" label="9. Pollution of water" />
                                        </div>
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 8 }}>
                                            <CheckboxItem name="hazardousWasteRatio" label="10. Hazardous waste ratio" />
                                        </div>
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 8 }}>
                                            <CheckboxItem name="deforestation" label="16. Deforestation (additional)" />
                                        </div>
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 12 }}>
                                            <p>
                                                <u>
                                                    Indicators for social and employee, respect for human rights, anti-corruption, and
                                                    anti-bribery matters
                                                </u>
                                            </p>
                                        </div>
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 4 }}>
                                            <CheckboxItem
                                                name="recordableWorkAccidents"
                                                label="2. Rate of recordable work-related accidents (additional)"
                                            />
                                        </div>
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 8 }}>
                                            <CheckboxItem
                                                name="lackOfProcessesAndCompliance"
                                                label="11. Lack of processes and compliance mechanisms to monitor compliance with OECD
                                                Guidelines for Multinational Enterprises or the UN Guiding principles, including the
                                                principles and rights set out in the eight fundamental conventions identified in the ILO Declaration and the International Bill of Human Rights (additional)"
                                            />
                                        </div>
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 8 }}>
                                            <CheckboxItem
                                                name="unadjustedGenderPayGap"
                                                label="12. Unadjusted gender pay gap between female and employees"
                                            />
                                        </div>
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 8 }}>
                                            <CheckboxItem
                                                name="managementSupervisoryGenderDiversity"
                                                label="13. Management and supervisory board gender diversity"
                                            />
                                        </div>
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 8 }}>
                                            <label className="font-bold">Environmental, social and governance</label>
                                        </div>
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 8 }}>
                                            <label>Global standard screen</label>
                                        </div>
                                        <FieldArray
                                            name="esg.globalStandardScreen"
                                            render={(arrayHelpers) => (
                                                <Fragment>
                                                    {values.esg &&
                                                    values.esg.globalStandardScreen &&
                                                    values.esg.globalStandardScreen.length > 0 ? (
                                                        values.esg.globalStandardScreen.map((item, index) => (
                                                            <div key={index} className="">
                                                                <div className="flex flex-wrap -mx-2">
                                                                    <SelectField
                                                                        name={`esg.globalStandardScreen[${index}].status`}
                                                                        label={"Status*"}
                                                                        options={Object.keys(StandardScreenStatus).sort()}
                                                                        className="w-full md:w-5/12 px-2 mb-4"
                                                                        disabled={isSubmitting}
                                                                        size={12}
                                                                    />
                                                                    <SelectField
                                                                        name={`esg.globalStandardScreen[${index}].norm`}
                                                                        label={"Norm*"}
                                                                        options={Object.keys(StandardScreenNorm).sort()}
                                                                        className="w-full md:w-5/12 px-2 mb-4"
                                                                        disabled={isSubmitting}
                                                                        size={12}
                                                                    />
                                                                </div>
                                                                <div className="flex flex-wrap -mx-2">
                                                                    <SelectField
                                                                        name={`esg.globalStandardScreen[${index}].principle`}
                                                                        label={"Principle*"}
                                                                        options={Object.keys(StandardScreenPrinciple).sort()}
                                                                        className="w-full md:w-5/12 px-2 mb-4"
                                                                        disabled={isSubmitting}
                                                                        size={12}
                                                                    />
                                                                    <DateField
                                                                        className="w-full md:w-5/12 px-2 mb-4"
                                                                        name={`esg.globalStandardScreen[${index}].date`}
                                                                        label="Date*"
                                                                        disabled={isSubmitting}
                                                                    />
                                                                </div>
                                                                <div className="flex flex-wrap -mx-2">
                                                                    <TextField
                                                                        name={`esg.globalStandardScreen[${index}].comment`}
                                                                        label="Comment*"
                                                                        className="w-full md:w-5/12 px-2 mb-4"
                                                                        disabled={false}
                                                                    />
                                                                </div>
                                                                <div className="flex flex-wrap -mx-2">
                                                                    <div className="w-full md:w-3/12 px-2 mb-4">
                                                                        <Button
                                                                            variant="danger"
                                                                            size="sm"
                                                                            className="mr-1 mt-4"
                                                                            type="button"
                                                                            onClick={() => arrayHelpers.remove(index)}
                                                                        >
                                                                            -
                                                                        </Button>

                                                                        <Button
                                                                            variant="primary"
                                                                            size="sm"
                                                                            className="mt-4"
                                                                            type="button"
                                                                            onClick={() =>
                                                                                arrayHelpers.insert(
                                                                                    index,
                                                                                    cloneDeep(defaultGlobalStandardScreensDefinition)
                                                                                )
                                                                            }
                                                                        >
                                                                            +
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="flex flex-wrap -mx-2">
                                                            <div className="w-full md:w-1/12 px-2 mb-4">
                                                                <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    className="ml-1"
                                                                    type="button"
                                                                    onClick={() =>
                                                                        arrayHelpers.push(cloneDeep(defaultGlobalStandardScreensDefinition))
                                                                    }
                                                                >
                                                                    +
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Fragment>
                                            )}
                                        />
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 14 }}>
                                            <label>Shareholder engagement</label>
                                        </div>
                                        <FieldArray
                                            name="esg.shareholderEngagement"
                                            render={(arrayHelpers) => (
                                                <Fragment>
                                                    {values.esg &&
                                                    values.esg.shareholderEngagement &&
                                                    values.esg.shareholderEngagement.length > 0 ? (
                                                        values.esg.shareholderEngagement.map((item, index) => (
                                                            <div key={index} className="">
                                                                <div className="flex flex-wrap -mx-2">
                                                                    <SelectField
                                                                        name={`esg.shareholderEngagement[${index}].method`}
                                                                        label={"Status*"}
                                                                        options={Object.keys(EngagementMethod).sort()}
                                                                        className="w-full md:w-5/12 px-2 mb-4"
                                                                        disabled={isSubmitting}
                                                                        size={12}
                                                                    />

                                                                    <DateField
                                                                        className="w-full md:w-5/12 px-2 mb-4"
                                                                        name={`esg.shareholderEngagement[${index}].date`}
                                                                        label="Date*"
                                                                        disabled={isSubmitting}
                                                                    />
                                                                </div>
                                                                <div className="flex flex-wrap -mx-2">
                                                                    <SelectField
                                                                        name={`esg.shareholderEngagement[${index}].principle`}
                                                                        label={"Principle*"}
                                                                        options={Object.keys(StandardScreenPrinciple).sort()}
                                                                        className="w-full md:w-5/12 px-2 mb-4"
                                                                        disabled={isSubmitting}
                                                                        size={12}
                                                                    />
                                                                    <DateField
                                                                        className="w-full md:w-5/12 px-2 mb-4"
                                                                        name={`esg.shareholderEngagement[${index}].agm`}
                                                                        label="Agm*"
                                                                        disabled={isSubmitting}
                                                                    />
                                                                </div>
                                                                <div className="flex flex-wrap -mx-2">
                                                                    <SelectField
                                                                        name={`esg.shareholderEngagement[${index}].norm`}
                                                                        label={"Norm*"}
                                                                        options={Object.keys(StandardScreenNorm).sort()}
                                                                        className="w-full md:w-5/12 px-2 mb-4"
                                                                        disabled={isSubmitting}
                                                                        size={12}
                                                                    />
                                                                    <TextField
                                                                        name={`esg.shareholderEngagement[${index}].comment`}
                                                                        label="Comment*"
                                                                        className="w-full md:w-5/12 px-2 mb-4"
                                                                        disabled={false}
                                                                    />
                                                                </div>
                                                                <div className="flex flex-wrap -mx-2">
                                                                    <div className="w-full md:w-3/12 px-2 mb-4">
                                                                        <Button
                                                                            variant="danger"
                                                                            size="sm"
                                                                            className="mr-1 mt-4"
                                                                            type="button"
                                                                            onClick={() => arrayHelpers.remove(index)}
                                                                        >
                                                                            -
                                                                        </Button>
                                                                        <Button
                                                                            variant="primary"
                                                                            size="sm"
                                                                            className="mt-4"
                                                                            type="button"
                                                                            onClick={() =>
                                                                                arrayHelpers.insert(
                                                                                    index,
                                                                                    cloneDeep(defaultShareholderEngagementsDefinition)
                                                                                )
                                                                            }
                                                                        >
                                                                            +
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="flex flex-wrap -mx-2">
                                                            <div className="w-full md:w-1/12 px-2 mb-4">
                                                                <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    className="ml-1"
                                                                    type="button"
                                                                    onClick={() =>
                                                                        arrayHelpers.push(
                                                                            cloneDeep(defaultShareholderEngagementsDefinition)
                                                                        )
                                                                    }
                                                                >
                                                                    +
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Fragment>
                                            )}
                                        />
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 14 }}>
                                            <div className="w-full px-2 mb-4">
                                                <label>Product involvement</label>
                                            </div>
                                        </div>
                                        <FieldArray
                                            name="esg.productInvolvement"
                                            render={(arrayHelpers) => (
                                                <Fragment>
                                                    {values.esg &&
                                                    values.esg.productInvolvement &&
                                                    values.esg.productInvolvement.length > 0 ? (
                                                        values.esg.productInvolvement.map((item, index) => (
                                                            <div key={index} className="">
                                                                <div className="flex flex-wrap -mx-2">
                                                                    <SelectField
                                                                        name={`esg.productInvolvement[${index}].involvement`}
                                                                        label={"Involvement*"}
                                                                        options={Object.keys(ProductInvolvementIndicator).sort()}
                                                                        className="w-full md:w-5/12 px-2 mb-4"
                                                                        disabled={isSubmitting}
                                                                        size={12}
                                                                    />
                                                                    <SelectField
                                                                        name={`esg.productInvolvement[${index}].range`}
                                                                        label={"Range*"}
                                                                        options={Object.keys(RangeEnum).sort()}
                                                                        className="w-full md:w-5/12 px-2 mb-4"
                                                                        disabled={isSubmitting}
                                                                        size={12}
                                                                    />
                                                                </div>
                                                                <div className="flex flex-wrap -mx-2">
                                                                    <div className="w-full md:w-5/12 px-2 mb-4">
                                                                        <Button
                                                                            variant="danger"
                                                                            size="sm"
                                                                            className="mr-1 mt-4"
                                                                            type="button"
                                                                            onClick={() => arrayHelpers.remove(index)}
                                                                        >
                                                                            -
                                                                        </Button>
                                                                        <Button
                                                                            variant="primary"
                                                                            size="sm"
                                                                            className="mt-4"
                                                                            type="button"
                                                                            onClick={() =>
                                                                                arrayHelpers.insert(
                                                                                    index,
                                                                                    cloneDeep(defaultProductInvolvementDefinition)
                                                                                )
                                                                            }
                                                                        >
                                                                            +
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="flex flex-wrap -mx-2">
                                                            <div className="w-full md:w-1/12 px-2 mb-4">
                                                                <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    className="ml-1"
                                                                    type="button"
                                                                    onClick={() =>
                                                                        arrayHelpers.push(cloneDeep(defaultProductInvolvementDefinition))
                                                                    }
                                                                >
                                                                    +
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Fragment>
                                            )}
                                        />
                                        <div className="flex flex-wrap -mx-2" style={{ marginTop: 14 }}>
                                            <div className="w-full px-2 mb-4">
                                                <label>Comments</label>
                                            </div>
                                        </div>
                                        <FieldArray
                                            name="esg.comments"
                                            render={(arrayHelpers) => (
                                                <Fragment>
                                                    {values.esg && values.esg.comments && values.esg.comments.length > 0 ? (
                                                        values.esg.comments.map((item, index) => (
                                                            <div key={index} className="">
                                                                <div className="flex flex-wrap -mx-2">
                                                                    <TextField
                                                                        name={`esg.comments[${index}].comment`}
                                                                        label="Comment*"
                                                                        className="w-full md:w-5/12 px-2 mb-4"
                                                                        disabled={false}
                                                                    />
                                                                    <DateField
                                                                        className="w-full md:w-5/12 px-2 mb-4"
                                                                        name={`esg.comments[${index}].date`}
                                                                        label="date*"
                                                                        disabled={isSubmitting}
                                                                    />
                                                                </div>
                                                                <div className="flex flex-wrap -mx-2">
                                                                    <div className="w-full md:w-5/12 px-2 mb-4">
                                                                        <Button
                                                                            variant="danger"
                                                                            size="sm"
                                                                            className="mr-1 mt-4"
                                                                            type="button"
                                                                            onClick={() => arrayHelpers.remove(index)}
                                                                        >
                                                                            -
                                                                        </Button>
                                                                        <Button
                                                                            variant="primary"
                                                                            size="sm"
                                                                            className="mt-4"
                                                                            type="button"
                                                                            onClick={() =>
                                                                                arrayHelpers.insert(
                                                                                    index,
                                                                                    cloneDeep(defaultCommentsDefinition)
                                                                                )
                                                                            }
                                                                        >
                                                                            +
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="flex flex-wrap -mx-2">
                                                            <div className="w-full md:w-1/12 px-2 mb-4">
                                                                <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    className="ml-1"
                                                                    type="button"
                                                                    onClick={() => arrayHelpers.push(cloneDeep(defaultCommentsDefinition))}
                                                                >
                                                                    +
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Fragment>
                                            )}
                                        />
                                        <div className="flex flex-wrap -mx-2 mt-4">
                                            <SelectField
                                                name="scienceBasedTargetInitiative"
                                                label="Science based target tnitiative"
                                                options={Object.keys(ScienceBasedTargetInitiativeEnum).sort()}
                                                className="w-full md:w-5/12 px-2 mb-4"
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                        <div className="flex flex-wrap -mx-2 ml-1" style={{ marginTop: 8 }}>
                                            <CheckboxItem name="netZeroCommitted" label="Net zero committed" />
                                        </div>
                                        <div className="flex flex-wrap -mx-2">
                                            <MarkDownField
                                                name="nearTermTargetClassification"
                                                graphqlApiBaseUrl={REACT_APP_API_URI}
                                                label="Near term target classification"
                                                className="w-full md:w-5/12 px-2 mb-4"
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                        {alert.visible ? (
                                            <Alert variant={alert.color} style={{ marginTop: "10px" }} onClose={onDismissAlert} dismissible>
                                                {alert.message}
                                            </Alert>
                                        ) : null}
                                        <SubmitButton disabled={isSubmitting} label="Update" />
                                    </Form>
                                </div>
                                <div className="w-full md:w-6/12 px-2 mb-4">
                                    <Grid
                                        header="Instruments"
                                        data={data.issuerInstruments}
                                        tableClassName="table-xs issuerinstrumentsgrid"
                                    >
                                        <Column
                                            field="name"
                                            className="grid-column-sticky"
                                            format={(value, item) => {
                                                return (
                                                    <Link to={`/instruments/${item._id}`}>{value}</Link>
                                                );
                                            }}
                                        />
                                        <Column field="currency" />
                                    </Grid>

                                    <Grid header="Issuer programs" data={data.issuerPrograms} tableClassName="table-xs issuerprogramsgrid">
                                        <Column
                                            field="name"
                                            format={(value, item) => {
                                                return (
                                                    <Link to={`/issuerprograms/${item._id}`}>{value}</Link>
                                                );
                                            }}
                                        />
                                        <Column field="category" />
                                        <Column
                                            field="spoProvider.name"
                                            title="SPO provider"
                                            format={(value, item) => {
                                                return (
                                                    item.spoProvider ? (
                                                        <Link to={`/parties/${item.spoProvider._id}`}>{value}</Link>
                                                    ) : (
                                                        value
                                                    )
                                                );
                                            }}
                                        />
                                    </Grid>
                                </div>
                            </div>
                        </Fragment>
                    );
                }}
            </Formik>
        </div>
    );
};
